import {
  leadsLogsListQueryKey,
  logsNoteCreateMutation,
  opportunitiesLogsListQueryKey
} from '@crm/api'
import { LogType } from '@crm/types'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { Button, Flex, Form, Input } from 'antd'

type KanbanCommentsType = 'opportunity' | 'lead'

type Props = {
  entityId?: number
  type?: KanbanCommentsType
  onCancel: () => void
}

export const NoteForm = ({ entityId, type, onCancel }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutateAsync: postMutation, isPending: isPendingPost } = useMutation({
    ...logsNoteCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: async () => await onSuccessMutation()
  })

  const onSuccessMutation = async () => {
    await invalidateQueries([
      opportunitiesLogsListQueryKey({
        path: { opportunity_id: String(entityId) }
      }),
      leadsLogsListQueryKey({
        path: { lead_id: String(entityId) }
      })
    ])

    form.resetFields()
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    const body = {
      description: values?.description,
      opportunity: type === 'opportunity' ? entityId : undefined,
      lead: type === 'lead' ? entityId : undefined,
      action: 'updated',
      type: LogType.NOTE
    }

    await postMutation({
      body
    })
  }

  return (
    <Form form={form}>
      <Form.Item name="description" rules={[{ required: true }]}>
        <Input.TextArea placeholder={t('logs.note')} rows={4} />
      </Form.Item>
      <Flex justify="center" gap={8}>
        <Button onClick={onCancel}>{t('buttons.cancel')}</Button>
        <Button
          type="primary"
          htmlType="submit"
          onClick={onSubmit}
          loading={isPendingPost}
        >
          {t('buttons.save')}
        </Button>
      </Flex>
    </Form>
  )
}
